import { InvoiceItemModel } from "#src/models/InvoiceItemModel.ts";
import InvoiceModel from "#src/models/InvoiceModel.ts";
import { moment } from "#src/utils/dateTime.ts";
import formatMoney from "#src/utils/formatMoney.ts";
import tw from "#src/utils/tw.ts";
import React from "react";
import Container from "./Container";
import {
  LineItem,
  LineItemButton,
  LineItemLabel,
  LineItemTotal,
} from "./LineItems";

interface Props {
  invoice: InvoiceModel;
  removeInvoiceItem?: (invoiceItem: InvoiceItemModel) => void;
  fullWidth?: boolean;
  totalString?: string;
  totalType?: string;
  small?: boolean;
  showTotal?: boolean;
  total?: number;
}

export default function PaymentTable(props: Props) {
  const today = moment();
  let totalString = "Total";

  // console.log("invoice", invoice);

  if (
    props.invoice.invoiceDate &&
    moment(props.invoice.invoiceDate).isSame(today, "day")
  ) {
    totalString = "Total Due Today";
  }

  if (props.totalString) {
    totalString = props.totalString;
  }

  let total = props.invoice.getTotals()[props.totalType || "totalOwed"];

  if (props.total) {
    total = props.total;
  }

  const totalAmount = (
    <span className={tw(total < 0 ? "text-success" : "text-dark")}>
      {total < 0 && "- "}${formatMoney(Math.abs(total))}
    </span>
  );

  const subtotalItem = props.invoice.invoiceItems.find(
    (ii) => ii.description === "Subtotal"
  );
  const fees = props.invoice.invoiceItems.filter(
    (ii) =>
      !ii.credit &&
      ii.description !== "Session Discount" &&
      ii.description !== "Subtotal"
  );
  const credits = props.invoice.invoiceItems
    .filter((ii) => ii.credit || ii.description === "Session Discount")
    .sort((a, b) => {
      if (
        a.description === "ACH Line Item Credit" ||
        b.description === "ACH Line Item Credit"
      ) {
        return 1;
      }

      return a.amount - b.amount;
    });

  const invoiceItems = [subtotalItem, ...credits, ...fees].filter(Boolean);

  return (
    <Container className={props.fullWidth ? "-mr-4 -ml-4" : "mb-4 ml-6"}>
      {invoiceItems
        .filter((a) => !a.credit)
        .map((i) => {
          let description = i.description;
          let meta;
          const isRemoveable =
            (i.charge ||
              (i.credit && i.credit.billingCategory) ||
              i.addon ||
              i.description.includes("Adding")) &&
            !i.notRemoveable;

          const isCredit =
            i.credit ||
            i.recurringCredit ||
            (i.discounts && i.discounts.length > 0) ||
            (i.coupons && i.coupons.length > 0 && !i.fee) ||
            i.amount < 0;

          if (i.charge || i.credit) {
            description = i.charge ? "One-time Charge" : "One-time Credit";

            if (i.charge) {
              meta = (
                <div className="text-sm">
                  {i.charge && i.charge.billingCategory && (
                    <div>{i.charge.billingCategory.title}</div>
                  )}
                  {i.charge.description}
                </div>
              );
            } else {
              meta = (
                <div className="text-sm">
                  {i.credit && i.credit.billingCategory && (
                    <div>{i.credit.billingCategory.title}</div>
                  )}
                  {i.credit.description}
                </div>
              );
            }
          }

          if (i.addon) {
            description = `Add-on: ${i.description} • ${i.quantity}`;

            if (i.description) {
              description += ` (${i.description})`;
            }
          }

          return (
            <React.Fragment key={i.id}>
              <LineItem>
                {isRemoveable && (
                  <LineItemButton onClick={() => props.removeInvoiceItem(i)}>
                    <i
                      aria-hidden
                      className="far fa-trash-can text-organization"
                    />
                  </LineItemButton>
                )}
                <LineItemLabel>
                  {description}
                  {meta}
                </LineItemLabel>
                <span className={tw(isCredit ? "text-success" : "text-dark")}>
                  {isCredit && "- "}${formatMoney(Math.abs(i.amount))}
                </span>
              </LineItem>
            </React.Fragment>
          );
        })}

      {(props.invoice.invoiceItems.length > 0 || props.showTotal) && (
        <>
          {(!props.showTotal || props.invoice.invoiceItems.length > 0) && (
            <hr />
          )}

          {invoiceItems
            .filter((a) => a.credit)
            .map((i) => {
              const isRemoveable =
                i.credit && i.credit.billingCategory && !i.notRemoveable;
              const description = "One-time Credit";

              const meta = (
                <div className="text-sm">
                  {i.credit && i.credit.billingCategory && (
                    <div>{i.credit.billingCategory.title}</div>
                  )}
                  {i.credit.description}
                </div>
              );

              return (
                <React.Fragment key={i.id}>
                  <LineItem>
                    {isRemoveable && (
                      <LineItemButton
                        onClick={() => props.removeInvoiceItem(i)}
                      >
                        <i
                          aria-hidden
                          className="far fa-trash-can text-organization"
                        />
                      </LineItemButton>
                    )}
                    <LineItemLabel>
                      {description}
                      {meta}
                    </LineItemLabel>
                    <span className="text-success">
                      - ${formatMoney(Math.abs(i.amount))}
                    </span>
                  </LineItem>
                </React.Fragment>
              );
            })}
          <LineItem className="pt-2">
            <LineItemLabel>{totalString}</LineItemLabel>
            {props.small && totalAmount}
            {!props.small && <LineItemTotal>{totalAmount}</LineItemTotal>}
          </LineItem>
        </>
      )}

      {props.invoice.invoiceItems.find(
        (ii) => !!ii.credit && !!ii.credit.billingCategory
      ) && (
        <p className="text-empty">
          Credits are not processed as refunds. The credit is applied to account
          balance.
        </p>
      )}
    </Container>
  );
}
